import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';
import { CandidateSearchableParam } from '../../models/candidate-search';

const sharedFeature: Pick<LocDef, 'feature'> = {
	feature: HubFeature.Core,
};

const sharedData: Pick<LocDef, 'htmlAllowed' | 'shared' | 'portal'> = {
	htmlAllowed: false,
	shared: true,
	portal: 'SP',
};

type SPCandidateSearchableLoc =
	| 'SPCandidateSearchFormSearchOptCandidateId'
	| 'SPCandidateSearchFormSearchOptEmail'
	| 'SPCandidateSearchFormSearchOptExamRegId'
	| 'SPCandidateSearchFormSearchOptExamAuthId'
	| 'SPCandidateSearchFormSearchOptDemographics'
	| 'SPCandidateSearchFormSearchOptAttributes'
	| 'SPCandidateSearchFormSearchOptAffId'
	| 'SPCandidateSearchFormSearchOptCMCId';
export type SPCandidateSearchFormLocs =
	| SPCandidateSearchableLoc
	| 'SPCandidateSearchFormSearchLabel'
	| 'SPCandidateSearchFormSearchOptCandidateId'
	| 'SPCandidateSearchFormSearchOptEmail'
	| 'SPCandidateSearchFormSearchOptExamRegId'
	| 'SPCandidateSearchFormSearchOptExamAuthId'
	| 'SPCandidateSearchFormSearchOptDemographics'
	| 'SPCandidateSearchFormSearchOptAttributes'
	| 'SPCandidateSearchFormSearchOptAffId'
	| 'SPCandidateSearchFormSearchOptCMCId'
	| 'SPCandidateSearchFormFirstNameLabel'
	| 'SPCandidateSearchFormLastNameLabel'
	| 'SPCandidateSearchFormCityLabel'
	| 'SPCandidateSearchFormStateLabel'
	| 'SPCandidateSearchFormCountryLabel'
	| 'SPCandidateSearchFormCountryErrorInvalid'
	| 'SPCandidateSearchFormAttributeLabel'
	| 'SPCandidateSearchFormAttributeErrorRequired'
	| 'SPCandidateSearchFormForLabel'
	| 'SPCandidateSearchFormForErrorRequired'
	| 'SPCandidateSearchFormSearchAction'
	| 'SPCandidateSearchFormForErrorMinLength';

export const spCandidateSearchFormLocs: LocDefs<SPCandidateSearchFormLocs> = {
	SPCandidateSearchFormSearchLabel: {
		defaultValue: 'Search',
		description: 'Label for the search type field.',
		legacyName: 'Core/System.CandidateSearch.FieldLabel.Search',
		displayPath: 'System|Candidate Search|Search Label',
		displayType: 'Label',
		...sharedData,
		...sharedFeature,
	},
	SPCandidateSearchFormForLabel: {
		defaultValue: 'For',
		description: 'Label for the search value field.',
		legacyName: 'Core/System.CandidateSearch.FieldLabel.For',
		displayPath: 'System|Candidate Search|For Label',
		displayType: 'Label',
		...sharedData,
		...sharedFeature,
	},
	SPCandidateSearchFormSearchOptCandidateId: {
		defaultValue: 'Candidate ID',
		description: 'Label for the candidate login search type',
		legacyName: 'Core/System.CandidateSearch.FieldLabel.ExternalID',
		displayPath: 'System|Candidate Search|External ID',
		displayType: 'Label',
		...sharedData,
		...sharedFeature,
	},
	SPCandidateSearchFormSearchOptEmail: {
		defaultValue: 'Email Address',
		description: 'Label for the candidate email address search type',
		legacyName:
			'CandidateProfile.PrimaryInformation/System.CandidateSearch.FieldLabel.EmailAddress',
		displayPath: 'System|Candidate Search|Email Address',
		displayType: 'Label',
		...sharedData,
		feature: HubFeature.CandidateProfilePrimaryInformation,
	},
	SPCandidateSearchFormSearchOptExamRegId: {
		defaultValue: 'Exam Registration ID',
		description: 'Label for the candidate exam registration ID search type',
		legacyName: 'Exam/System.CandidateSearch.FieldLabel.ExamRegistrationID',
		displayPath: 'System|Candidate Search|Exam Registration ID',
		displayType: 'Label',
		...sharedData,
		feature: HubFeature.Exam,
	},
	SPCandidateSearchFormSearchOptExamAuthId: {
		defaultValue: 'Exam Authorization ID',
		description: 'Label for the candidate exam authorization ID search type',
		legacyName: 'Exam.Authorization/System.CandidateSearch.FieldLabel.ExamAuthorizationID',
		displayPath: 'System|Candidate Search|Exam Authorization ID',
		displayType: 'Label',
		...sharedData,
		feature: HubFeature.ExamAuthorization,
	},
	SPCandidateSearchFormSearchOptDemographics: {
		defaultValue: 'Demographics',
		description: 'Label for the candidate demographic search type',
		legacyName: 'CandidateProfile/System.CandidateSearch.FieldLabel.Demographics',
		displayPath: 'System|Candidate Search|Demographics',
		displayType: 'Label',
		...sharedData,
		feature: HubFeature.CandidateProfile,
	},
	SPCandidateSearchFormSearchOptAttributes: {
		defaultValue: 'Attributes',
		description: 'Label for the candidate attribute search type',
		legacyName:
			'CandidateProfile.CustomData.Attributes/System.CandidateSearch.FieldLabel.Attributes',
		displayPath: 'System|Candidate Search|Attributes',
		displayType: 'Label',
		...sharedData,
		feature: HubFeature.CandidateProfileCustomDataAttributes,
	},
	SPCandidateSearchFormSearchOptAffId: {
		defaultValue: 'Affiliation ID',
		description: 'Label for the candidate affiliation ID search type',
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/System.CandidateSearch.FieldLabel.AffiliationID',
		displayPath: 'System|Candidate Search|Affiliation ID',
		displayType: 'Label',
		...sharedData,
		feature: HubFeature.CandidateProfilePrimaryInformationAffiliation,
	},
	SPCandidateSearchFormSearchOptCMCId: {
		defaultValue: 'CMC ID',
		description: 'Label for the candidate CMC ID search type',
		legacyName: 'Core/System.CandidateSearch.FieldLabel.CMCID',
		displayPath: 'System|Candidate Search|CMC ID',
		displayType: 'Label',
		...sharedData,
		...sharedFeature,
	},
	SPCandidateSearchFormFirstNameLabel: {
		defaultValue: 'First Name',
		description: 'Label for the first name search field',
		legacyName:
			'CandidateProfile.PrimaryInformation/System.CandidateSearch.FieldLabel.FirstName',
		displayPath: 'System|Candidate Search|Demographics|First Name',
		displayType: 'Label',
		...sharedData,
		feature: HubFeature.CandidateProfilePrimaryInformation,
	},
	SPCandidateSearchFormLastNameLabel: {
		defaultValue: 'Last Name',
		description:
			'CandidateProfile.PrimaryInformation/System.CandidateSearch.FieldLabel.LastName',
		legacyName: 'Label for the last name search field',
		displayPath: 'System|Candidate Search|Demographics|Last Name',
		displayType: 'Label',
		...sharedData,
		feature: HubFeature.CandidateProfilePrimaryInformation,
	},
	SPCandidateSearchFormCityLabel: {
		defaultValue: 'City',
		description: 'Label for the city search field',
		legacyName: 'CandidateProfile.PrimaryInformation/System.CandidateSearch.FieldLabel.City',
		displayPath: 'System|Candidate Search|Demographics|City',
		displayType: 'Label',
		...sharedData,
		feature: HubFeature.CandidateProfilePrimaryInformation,
	},
	SPCandidateSearchFormStateLabel: {
		defaultValue: 'State/Province',
		description: 'Label for the state/province search field',
		legacyName:
			'CandidateProfile.PrimaryInformation/System.CandidateSearch.FieldLabel.Subdivision',
		displayPath: 'System|Candidate Search|Demographics|State/Province',
		displayType: 'Label',
		...sharedData,
		feature: HubFeature.CandidateProfilePrimaryInformation,
	},
	SPCandidateSearchFormCountryLabel: {
		defaultValue: 'Country',
		description: 'Label for the country search field',
		legacyName: 'CandidateProfile.PrimaryInformation/System.CandidateSearch.FieldLabel.Country',
		displayPath: 'System|Candidate Search|Demographics|Country',
		displayType: 'Label',
		...sharedData,
		feature: HubFeature.CandidateProfilePrimaryInformation,
	},
	SPCandidateSearchFormCountryErrorInvalid: {
		defaultValue: 'Invalid country',
		description: 'Error message when the selected country is invalid',
		displayPath: 'System|Candidate Search|Demographics|Invalid Country',
		displayType: 'Error',
		...sharedData,
		feature: HubFeature.CandidateProfilePrimaryInformation,
	},
	SPCandidateSearchFormAttributeLabel: {
		defaultValue: 'Attribute',
		description: 'Label for the attribute name field',
		legacyName:
			'CandidateProfile.CustomData.Attributes/System.CandidateSearch.FieldLabel.AttributeName',
		displayPath: 'System|Candidate Search|Attributes|Attribute Name',
		displayType: 'Label',
		...sharedData,
		feature: HubFeature.CandidateProfileCustomDataAttributes,
	},
	SPCandidateSearchFormAttributeErrorRequired: {
		defaultValue: 'Attribute is required',
		description: 'Error message when attribute name is empty',
		legacyName:
			'CandidateProfile.CustomData.Attributes/System.CandidateSearch.FieldError.AttributeNameRequired',
		displayPath: 'System|Candidate Search|Attributes|Attribute Name Required',
		displayType: 'Error',
		...sharedData,
		feature: HubFeature.CandidateProfileCustomDataAttributes,
	},
	SPCandidateSearchFormForErrorRequired: {
		defaultValue: 'Search value is required',
		description: 'Error when the search value is empty',
		legacyName: 'Core/System.CandidateSearch.FieldError.SearchRequired',
		displayPath: 'System|Candidate Search|Search Required',
		displayType: 'Error',
		...sharedData,
		...sharedFeature,
	},
	SPCandidateSearchFormForErrorMinLength: {
		defaultValue: 'Must have at least two characters',
		description: 'Error when the search value is too short (minimum of 2 characters)',
		legacyName: 'Core/System.CandidateSearch.FieldError.MinLength',
		displayPath: 'System|Candidate Search|Minimum Length',
		displayType: 'Error',
		...sharedData,
		...sharedFeature,
	},
	SPCandidateSearchFormSearchAction: {
		defaultValue: 'Search',
		description: 'Submit button on the candidate search form',
		legacyName: 'Core/System.CandidateSearch.Button.Submit',
		displayPath: 'System|Candidate Search|Submit',
		displayType: 'Button',
		...sharedData,
		...sharedFeature,
	},
};

export type SPCandidateSearchFormLocKeys = LocMap<SPCandidateSearchFormLocs>;

export const spCandidateSearchFormLocKeys =
	MapLocKeys<SPCandidateSearchFormLocs>(spCandidateSearchFormLocs);

export const spCandidateSearchOptionMap: Record<
	CandidateSearchableParam,
	SPCandidateSearchableLoc
> = {
	emailAddr: 'SPCandidateSearchFormSearchOptEmail',
	examAuthorizationId: 'SPCandidateSearchFormSearchOptExamAuthId',
	examRegistrationId: 'SPCandidateSearchFormSearchOptExamRegId',
	affiliationId: 'SPCandidateSearchFormSearchOptAffId',
	demographics: 'SPCandidateSearchFormSearchOptDemographics',
	attributes: 'SPCandidateSearchFormSearchOptAttributes',
	cmcId: 'SPCandidateSearchFormSearchOptCMCId',
	externalId: 'SPCandidateSearchFormSearchOptCandidateId',
};
